import { useLocalSearchParams, useRouter } from "expo-router";
import { useState } from "react";
import { KeyboardAvoidingView, Platform, StyleSheet, View } from "react-native";
import { Button, Card, Input, Screen, Text } from "~/components/ui";
import { useAuth } from "~/lib/auth";
import { spacing, type ThemeColors } from "~/styles/tokens";
import { useTheme, useThemedStyles } from "~/styles/theme";

export default function ResetPasswordScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const params = useLocalSearchParams<{ token?: string }>();
  const token = typeof params.token === "string" ? params.token : "";
  const { ready, finishPasswordReset } = useAuth();
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  // The emailed link carries a one-time token; the API validates it when the
  // new password is submitted.
  const submit = async () => {
    if (!token || !password) return;
    setLoading(true);
    setMessage(null);
    try {
      const result = await finishPasswordReset(token, password);
      if (result.error) {
        setMessage(result.error);
        return;
      }
      setComplete(true);
      setMessage("Your password has been changed. Sign in with the new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <Screen scroll width="narrow" centered>
        <View style={styles.header}>
          <Text style={styles.logo}>🔑</Text>
          <Text variant="heading" align="center">
            Choose a new password
          </Text>
        </View>
        <Card elevation="raised" style={styles.formCard}>
          {!ready ? (
            <Text variant="body" color={colors.muted}>
              One moment…
            </Text>
          ) : !token ? (
            <Text variant="body" color={colors.danger}>
              This reset link is invalid or expired.
            </Text>
          ) : (
            <>
              {!complete ? (
                <>
                  <Input
                    label="New password"
                    placeholder="At least 12 characters"
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry
                    autoComplete="new-password"
                  />
                  <Button
                    label="Save my new password"
                    onPress={() => void submit()}
                    loading={loading}
                    variant="primary"
                    fullWidth
                    style={styles.submit}
                  />
                </>
              ) : null}
              {message ? (
                <Text
                  variant="caption"
                  color={complete ? colors.success : colors.danger}
                  style={styles.message}
                >
                  {message}
                </Text>
              ) : null}
              {complete ? (
                <Button
                  label="Sign in"
                  onPress={() => router.replace("/auth")}
                  variant="primary"
                  fullWidth
                  style={styles.submit}
                />
              ) : null}
            </>
          )}
        </Card>
      </Screen>
    </KeyboardAvoidingView>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    flex: {
      flex: 1,
    },
    header: {
      alignItems: "center",
      paddingBottom: spacing.lg,
      gap: spacing.xs,
    },
    logo: {
      fontSize: 48,
      lineHeight: 56,
    },
    formCard: {
      gap: spacing.xs,
      marginBottom: spacing.md,
    },
    message: {
      marginTop: spacing.xs,
    },
    submit: {
      marginTop: spacing.sm,
    },
  });
